import { getBackendUrl } from "@/lib/backend";
import { useEffect, useMemo, useRef, useState } from "react";
import axios, { API } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Sparkles, Loader2, X, MousePointerClick, Undo2, Hand, Square, Frame, Gem, ImagePlus } from "lucide-react";

import {
  MAX_AREAS, MAX_REF_KB, ALLOWED_REF_TYPES, REF_KIND_LABEL, BADGE_COLORS,
  MIN_POINT_DELTA, MIN_PATH_SIZE, MAX_POLYGON_POINTS,
  ZOOM_LEVELS, ZOOM_MIN, ZOOM_MAX, ZOOM_STEP,
  bbox, centroid, polygonPointsAttr, clamp, readAsDataURL, simplifyPolygon,
} from "@/lib/refineUtils";
import { formatApiError } from "@/lib/apiError";

const BACKEND = getBackendUrl();

// Quick-action chips. The `token` is what we prepend to the bullet
// the backend sees, e.g. `[action=setting]` — Gemini reads this as a
// structured intent signal alongside the free-text description.
const REFINE_ACTIONS = [
  { id: "setting", label: "Setting" },
  { id: "colour",  label: "Colour" },
  { id: "metal",   label: "Metal" },
  { id: "shape",   label: "Shape" },
  { id: "add",     label: "Add" },
  { id: "remove",  label: "Remove" },
];

// Metal palette. Token is `metal=<id>` — kept lowercase + underscored so
// the model sees a parseable key. We only list the four common gold/
// platinum tones; oxidised / antique tones stay in the free-text brief.
const REFINE_METALS = [
  { id: "yellow_gold",   label: "Yellow Gold" },
  { id: "rose_gold",     label: "Rose Gold" },
  { id: "white_gold",    label: "White Gold" },
  { id: "platinum",      label: "Platinum" },
];

/**
 * RefineDialog — free-form (lasso) area marking with translucent fills,
 * numbered badges, and a backend that composites an overlay mask.
 */
export default function RefineDialog({ open, onOpenChange, source, quotaRemaining = 0, onRefined }) {
  const imgWrapRef = useRef(null);
  const [areas, setAreas] = useState([]); // [{points:[{x,y}], comment}]
  const [drawing, setDrawing] = useState(null); // {points:[]}
  const [overall, setOverall] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [imgDims, setImgDims] = useState({ w: 0, h: 0 });

  // Zoom + pan state. `pan` is the image-normalized point (0..1) currently at the center of the viewport.
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0.5, y: 0.5 });
  const [panMode, setPanMode] = useState(false); // sticky toggle (toolbar)
  const [spaceHeld, setSpaceHeld] = useState(false); // momentary (Space key)
  const [panning, setPanning] = useState(null); // {startClientX, startClientY, startPanX, startPanY}

  // Recent references library (gemstone/texture/colour swatches saved per user)
  const [refLibrary, setRefLibrary] = useState([]);
  const [activeAreaIdx, setActiveAreaIdx] = useState(null);

  useEffect(() => {
    if (!open) return;
    axios
      .get(`${API}/studio/references`)
      .then((res) => setRefLibrary(res.data?.items || []))
      .catch(() => setRefLibrary([]));
  }, [open]);

  useEffect(() => {
    if (!open) {
      setAreas([]);
      setDrawing(null);
      setOverall("");
      setSubmitting(false);
      setZoom(1);
      setPan({ x: 0.5, y: 0.5 });
      setPanMode(false);
      setSpaceHeld(false);
      setPanning(null);
      setActiveAreaIdx(null);
    }
  }, [open]);

  // Space-hold for momentary pan (like Figma/Photoshop) + F-key fit-to-selection
  useEffect(() => {
    if (!open) return undefined;
    const isTypingTarget = (el) => {
      if (!el) return false;
      const tag = el.tagName;
      return tag === "INPUT" || tag === "TEXTAREA" || el.isContentEditable;
    };
    const onKeyDown = (e) => {
      if (isTypingTarget(e.target)) return;
      if (e.code === "Space") {
        e.preventDefault();
        setSpaceHeld(true);
        return;
      }
      // F = fit-to-selection (latest area). Numbers 1/2/3 = fit to area N.
      if (e.key === "f" || e.key === "F") {
        if (areas.length === 0) return;
        e.preventDefault();
        fitToArea();
        return;
      }
      if (/^[1-3]$/.test(e.key)) {
        const i = Number(e.key) - 1;
        if (i < areas.length) {
          e.preventDefault();
          fitToArea(i);
        }
        return;
      }
      // 0 / Escape from zoomed in resets the view
      if ((e.key === "0" || e.key === "Escape") && zoom !== 1) {
        e.preventDefault();
        setZoom(1);
        setPan({ x: 0.5, y: 0.5 });
        setPanMode(false);
      }
    };
    const onKeyUp = (e) => {
      if (e.code !== "Space") return;
      setSpaceHeld(false);
    };
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, areas, zoom]);

  const aspectRatio = imgDims.w && imgDims.h ? `${imgDims.w}/${imgDims.h}` : "1/1";

  // Clamp pan so the viewport never shows past the image edges.
  const clampPan = (p, z) => {
    const half = 0.5 / z;
    return {
      x: clamp(p.x, half, 1 - half),
      y: clamp(p.y, half, 1 - half),
    };
  };

  // Convert a screen-space (clientX, clientY) into the ORIGINAL image-normalized coordinate.
  const toNorm = (clientX, clientY) => {
    const r = imgWrapRef.current?.getBoundingClientRect();
    if (!r) return { x: 0, y: 0 };
    const cx = (clientX - r.left) / r.width; // viewport fraction 0..1
    const cy = (clientY - r.top) / r.height;
    // Reverse the CSS transform: viewport (cx,cy) → image-space coord
    return {
      x: clamp(pan.x + (cx - 0.5) / zoom, 0, 1),
      y: clamp(pan.y + (cy - 0.5) / zoom, 0, 1),
    };
  };

  // Apply a zoom level, optionally anchored at a viewport point (cx, cy in 0..1).
  // Anchored zoom keeps the image-coord under (cx, cy) stationary during the zoom.
  const applyZoom = (newZoomRaw, anchor = null) => {
    const newZoom = clamp(newZoomRaw, ZOOM_MIN, ZOOM_MAX);
    if (newZoom === zoom) return;
    if (anchor) {
      // Image-space coord currently at anchor:
      const px = pan.x + (anchor.cx - 0.5) / zoom;
      const py = pan.y + (anchor.cy - 0.5) / zoom;
      // We want it to remain there after the zoom change.
      const newPan = clampPan({
        x: px - (anchor.cx - 0.5) / newZoom,
        y: py - (anchor.cy - 0.5) / newZoom,
      }, newZoom);
      setPan(newPan);
    } else {
      setPan((p) => clampPan(p, newZoom));
    }
    setZoom(newZoom);
    if (newZoom === 1) setPanMode(false); // pan toggle meaningless at 1×
  };

  const onWheel = (e) => {
    // Only intercept when Ctrl/⌘ is held — matches Figma/Photoshop convention and
    // lets users keep page-scroll behaviour everywhere else.
    if (!(e.ctrlKey || e.metaKey)) return;
    e.preventDefault();
    const r = imgWrapRef.current?.getBoundingClientRect();
    if (!r) return;
    const cx = (e.clientX - r.left) / r.width;
    const cy = (e.clientY - r.top) / r.height;
    // Exponential feel: deltaY < 0 means zoom in
    const factor = Math.exp(-e.deltaY * 0.0015);
    applyZoom(zoom * factor, { cx, cy });
  };

  // Fit-to-selection: zoom + pan so the given area's bounding box fills the viewport
  // (with a little breathing room). Pass an index, or use the latest area by default.
  const fitToArea = (i = areas.length - 1) => {
    const a = areas[i];
    if (!a) return;
    const xs = a.points.map((p) => p.x);
    const ys = a.points.map((p) => p.y);
    const minX = Math.min(...xs), maxX = Math.max(...xs);
    const minY = Math.min(...ys), maxY = Math.max(...ys);
    const w = Math.max(maxX - minX, 0.02);
    const h = Math.max(maxY - minY, 0.02);
    // Pad ~25% on each side so the area doesn't kiss the viewport edge
    const padded = Math.max(w, h) * 1.5;
    const newZoom = clamp(1 / padded, ZOOM_MIN, ZOOM_MAX);
    const newPan = clampPan({ x: (minX + maxX) / 2, y: (minY + maxY) / 2 }, newZoom);
    setPan(newPan);
    setZoom(newZoom);
    if (newZoom === 1) setPanMode(false);
  };

  // Smart double-click zoom: cycles 1× → 2× → 4× → 1×, anchored at the click point.
  // Skip when in pan gesture so Space+dblclick still pans.
  const onDoubleClick = (e) => {
    if (isPanGesture) return;
    const r = imgWrapRef.current?.getBoundingClientRect();
    if (!r) return;
    e.preventDefault();
    // Discard any in-flight tiny drawing left over from the two clicks
    setDrawing(null);
    const cx = (e.clientX - r.left) / r.width;
    const cy = (e.clientY - r.top) / r.height;
    const next = zoom < 2 ? 2 : zoom < 4 ? 4 : 1;
    if (next === 1) {
      setZoom(1);
      setPan({ x: 0.5, y: 0.5 });
      setPanMode(false);
    } else {
      applyZoom(next, { cx, cy });
    }
  };

  const isPanGesture = (panMode || spaceHeld) && zoom > 1;

  const onPointerDown = (e) => {
    // Pan gesture takes priority when active
    if (isPanGesture) {
      e.preventDefault();
      try { e.target.setPointerCapture?.(e.pointerId); } catch { /* noop */ }
      setPanning({
        startClientX: e.clientX,
        startClientY: e.clientY,
        startPanX: pan.x,
        startPanY: pan.y,
      });
      return;
    }
    if (areas.length >= MAX_AREAS) {
      toast.error(`Maximum ${MAX_AREAS} areas. Remove one to add another.`);
      return;
    }
    e.preventDefault();
    const p = toNorm(e.clientX, e.clientY);
    // Use pointer capture so we keep tracking even when cursor briefly leaves the area
    try { e.target.setPointerCapture?.(e.pointerId); } catch { /* noop */ }
    setDrawing({ points: [p] });
  };

  const onPointerMove = (e) => {
    if (panning) {
      const r = imgWrapRef.current?.getBoundingClientRect();
      if (!r) return;
      // Drag right = look further left (image-space pan-x decreases)
      const dx = (e.clientX - panning.startClientX) / r.width / zoom;
      const dy = (e.clientY - panning.startClientY) / r.height / zoom;
      setPan(clampPan({
        x: panning.startPanX - dx,
        y: panning.startPanY - dy,
      }, zoom));
      return;
    }
    if (!drawing) return;
    const p = toNorm(e.clientX, e.clientY);
    // Functional setState — pointermove fires faster than React re-renders,
    // so reading `drawing.points` from the closure dropped every point
    // between renders (the bug that made the lasso path look like dots).
    setDrawing((d) => {
      if (!d) return d;
      const last = d.points[d.points.length - 1];
      const dx = p.x - last.x;
      const dy = p.y - last.y;
      if (Math.hypot(dx, dy) < MIN_POINT_DELTA) return d; // throttle
      return { points: [...d.points, p] };
    });
  };

  const finalizePath = () => {
    if (!drawing) return;
    const rawPts = drawing.points;
    const box = bbox(rawPts);
    if (rawPts.length < 4 || box.w < MIN_PATH_SIZE || box.h < MIN_PATH_SIZE) {
      setDrawing(null);
      return;
    }
    // Douglas-Peucker simplify to stay under the backend's 200-point
    // cap. Free-form lassos on a large region routinely produce 300+
    // raw points; without this, the user hits a 422 from the validator.
    // We target 180 to leave headroom and apply only when needed so
    // small/tight lassos stay pixel-exact.
    const pts = rawPts.length > MAX_POLYGON_POINTS
      ? simplifyPolygon(rawPts, MAX_POLYGON_POINTS)
      : rawPts;
    setAreas((a) => {
      const next = [...a, {
        id: `a_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`,
        points: pts,
        comment: "",
        action: null,        // one of REFINE_ACTIONS ids (or null)
        metal: null,         // one of REFINE_METALS ids (or null)
        reference_image_b64: null,
        reference_preview: null,
        reference_kind: "gemstone",
      }];
      setActiveAreaIdx(next.length - 1);
      return next;
    });
    setDrawing(null);
  };

  const onPointerUp = (e) => {
    try { e?.target?.releasePointerCapture?.(e.pointerId); } catch { /* noop */ }
    if (panning) {
      setPanning(null);
      return;
    }
    finalizePath();
  };

  const onPointerCancel = () => {
    setDrawing(null);
    setPanning(null);
  };

  const removeArea = (i) => {
    setAreas((a) => a.filter((_, idx) => idx !== i));
    setActiveAreaIdx((cur) => {
      if (cur == null) return cur;
      if (cur === i) return null;
      if (cur > i) return cur - 1;
      return cur;
    });
  };
  const undoLastPoint = () => {
    if (!drawing || drawing.points.length <= 1) return setDrawing(null);
    setDrawing({ points: drawing.points.slice(0, -1) });
  };
  const updateComment = (i, v) =>
    setAreas((a) => a.map((ar, idx) => (idx === i ? { ...ar, comment: v } : ar)));

  const updateRefKind = (i, v) =>
    setAreas((a) => a.map((ar, idx) => (idx === i ? { ...ar, reference_kind: v } : ar)));

  // Toggle the quick-action chip on an area card. Clicking the active chip
  // again clears it (so designers can switch to "free text only").
  const updateAction = (i, v) =>
    setAreas((a) =>
      a.map((ar, idx) =>
        idx === i ? { ...ar, action: ar.action === v ? null : v } : ar,
      ),
    );

  // Metal dropdown — empty string clears the selection.
  const updateMetal = (i, v) =>
    setAreas((a) =>
      a.map((ar, idx) => (idx === i ? { ...ar, metal: v || null } : ar)),
    );

  const attachReference = async (i, file) => {
    if (!file) return;
    if (!ALLOWED_REF_TYPES.includes(file.type)) {
      return toast.error("Use a PNG, JPG, or WEBP image.");
    }
    if (file.size > MAX_REF_KB * 1024) {
      return toast.error(`Image is too large. Keep under ${MAX_REF_KB / 1000} MB.`);
    }
    try {
      const dataUrl = await readAsDataURL(file);
      setAreas((a) =>
        a.map((ar, idx) =>
          idx === i ? { ...ar, reference_image_b64: dataUrl, reference_preview: dataUrl } : ar,
        ),
      );
    } catch (err) {
      console.error("attachReference (file read) failed", err);
      toast.error("Could not read that image. Try another.");
    }
  };

  const clearReference = (i) =>
    setAreas((a) =>
      a.map((ar, idx) =>
        idx === i ? { ...ar, reference_image_b64: null, reference_preview: null } : ar,
      ),
    );

  // Attach a saved library reference to an area without re-uploading.
  // Fetches the bytes as a base64 data URL so the existing refine payload
  // works unchanged on the backend.
  const attachFromLibrary = async (areaIdx, ref) => {
    try {
      const r = await axios.get(`${BACKEND}${ref.image_url}`, { responseType: "blob" });
      const blob = r.data;
      const dataUrl = await new Promise((resolve, reject) => {
        const fr = new FileReader();
        fr.onerror = () => reject(new Error("Read failed"));
        fr.onload = () => resolve(fr.result);
        fr.readAsDataURL(blob);
      });
      setAreas((a) =>
        a.map((ar, idx) =>
          idx === areaIdx
            ? {
                ...ar,
                reference_image_b64: dataUrl,
                reference_preview: dataUrl,
                reference_kind: ref.kind || ar.reference_kind || "gemstone",
              }
            : ar,
        ),
      );
      // Touch usage on the backend so it stays at the top of the library
      axios.post(`${API}/studio/references/${ref.id}/use`).catch((touchErr) => {
        console.warn("touch reference failed (non-fatal)", touchErr);
      });
      // Move this entry to the front of our local list too for instant feedback
      setRefLibrary((lib) => [ref, ...lib.filter((r2) => r2.id !== ref.id)]);
    } catch (err) {
      console.error("attachFromLibrary failed", err);
      toast.error("Could not attach that reference. Try again.");
    }
  };

  const deleteLibraryRef = async (ref) => {
    setRefLibrary((lib) => lib.filter((r) => r.id !== ref.id));
    try {
      await axios.delete(`${API}/studio/references/${ref.id}`);
    } catch (err) {
      console.error("deleteLibraryRef failed", err);
      toast.error("Could not delete reference.");
    }
  };

  const submit = async () => {
    if (areas.length === 0) return toast.error("Lasso at least one area to refine.");
    const empty = areas.findIndex((a) => !a.comment.trim() || a.comment.trim().length < 2);
    if (empty >= 0) return toast.error(`Add a comment for area ${empty + 1}.`);
    if (quotaRemaining < 1) return toast.error("No renders left this month.");

    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/studio/refine/${source.id}`, {
        areas: areas.map(({ points, comment, action, metal, reference_image_b64, reference_kind }) => {
          // Prepend a clean structured token block so the backend prompt
          // gets a parseable intent signal alongside the free text.
          // Example bullet sent: "[action=metal metal=yellow_gold] make the shank yellow gold"
          const tokens = [];
          if (action) tokens.push(`action=${action}`);
          if (metal)  tokens.push(`metal=${metal}`);
          const prefix = tokens.length ? `[${tokens.join(" ")}] ` : "";
          return {
            points: points.map((p) => ({ x: p.x, y: p.y })),
            comment: (prefix + comment.trim()).trim(),
            reference_image_b64: reference_image_b64 || null,
            reference_kind: reference_image_b64 ? (reference_kind || "gemstone") : null,
          };
        }),
        overall_note: overall.trim() || null,
      }, { timeout: 240000 });
      toast.success("Refinement ready.");
      onRefined?.(res.data.entry, res.data.quota);
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err, "Refinement failed."));
    } finally {
      setSubmitting(false);
    }
  };

  // Memoized render data for areas (centroids)
  const areaMeta = useMemo(
    () => areas.map((a) => ({ centroid: centroid(a.points), bbox: bbox(a.points) })),
    [areas]
  );

  if (!source) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="refine-dialog"
        className="bg-[#0E0E0E] border border-white/10 text-white p-0 rounded-2xl shadow-[0_30px_80px_-30px_rgba(0,0,0,0.8)] w-[96vw] max-w-[1200px] max-h-[88vh] overflow-hidden flex flex-col gap-0 sm:!rounded-2xl"
      >
        {/* Compact header */}
        <DialogHeader className="px-5 pt-4 pb-3 border-b border-white/8 space-y-1 flex-shrink-0">
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-[#D4AF37]">
                <Sparkles className="w-3 h-3" /> Refine · 1 render
              </div>
              <DialogTitle className="font-display text-lg md:text-xl tracking-tight text-white mt-0.5">
                Lasso up to {MAX_AREAS} areas to refine
              </DialogTitle>
            </div>
            <div className="hidden md:flex items-center gap-3 text-[11px] text-white/45">
              <span>{areas.length} / {MAX_AREAS} marked</span>
              <span className="text-white/25">·</span>
              <span>{quotaRemaining} renders left</span>
            </div>
          </div>
          <DialogDescription className="text-white/50 text-[11px] leading-snug">
            Drag around any region, then describe the change — the rest will be preserved.
            <span className="hidden md:inline"> · <kbd className="px-1 py-0.5 rounded bg-white/8 text-white/65 text-[10px]">Ctrl + scroll</kbd> zoom · <kbd className="px-1 py-0.5 rounded bg-white/8 text-white/65 text-[10px]">Double-click</kbd> step zoom · <kbd className="px-1 py-0.5 rounded bg-white/8 text-white/65 text-[10px]">F</kbd> fit area · <kbd className="px-1 py-0.5 rounded bg-white/8 text-white/65 text-[10px]">Space</kbd> pan</span>
          </DialogDescription>
        </DialogHeader>

        {/* Body: image (left) + comments (right) — fills remaining height, each side scrolls independently */}
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.55fr)_minmax(280px,1fr)] gap-4 px-5 py-4 flex-1 min-h-0 overflow-y-auto lg:overflow-hidden">
          {/* Image */}
          <div className="min-h-0 flex flex-col">
            <div
              ref={imgWrapRef}
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
              onPointerCancel={onPointerCancel}
              onDoubleClick={onDoubleClick}
              onWheel={onWheel}
              data-testid="refine-image-area"
              className="relative w-full select-none rounded-xl overflow-hidden border border-white/10 bg-[#0B0B0B] touch-none mx-auto"
              style={{
                aspectRatio,
                maxHeight: "min(56vh, 640px)",
                maxWidth: "100%",
                cursor:
                  isPanGesture
                    ? (panning ? "grabbing" : "grab")
                    : (areas.length >= MAX_AREAS ? "default" : "crosshair"),
              }}
            >
              {/* Transformed inner: image + SVG share the same transform so polygons stay glued to image pixels */}
              <div
                className="absolute inset-0 will-change-transform"
                style={{
                  transform: `scale(${zoom}) translate(${((0.5 - pan.x) * 100).toFixed(3)}%, ${((0.5 - pan.y) * 100).toFixed(3)}%)`,
                  transformOrigin: "center center",
                  transition: panning ? "none" : "transform 180ms ease",
                }}
              >
                <img
                  src={`${BACKEND}${source.image_url}`}
                  alt={source.prompt}
                  onLoad={(e) => setImgDims({ w: e.target.naturalWidth, h: e.target.naturalHeight })}
                  className="w-full h-full object-contain pointer-events-none"
                  draggable={false}
                />
                {/* Overlay rendered as SVG in normalized 0..100 space */}
                <svg
                  viewBox="0 0 100 100"
                  preserveAspectRatio="none"
                  className="absolute inset-0 w-full h-full pointer-events-none"
                >
                  {areas.map((a, i) => {
                    const color = BADGE_COLORS[i % BADGE_COLORS.length];
                    const c = areaMeta[i].centroid;
                    // Counter-scale the badge so the number stays readable at high zoom
                    const badgeScale = 1 / zoom;
                    return (
                      <g key={a.id || `area-${i}`}>
                        <polygon
                          points={polygonPointsAttr(a.points)}
                          fill={color}
                          fillOpacity="0.32"
                          stroke={color}
                          strokeWidth="0.6"
                          vectorEffect="non-scaling-stroke"
                          strokeLinejoin="round"
                        />
                        <g transform={`translate(${(c.x * 100).toFixed(2)}, ${(c.y * 100).toFixed(2)}) scale(${badgeScale})`}>
                          <circle
                            cx="0"
                            cy="0"
                            r="2.8"
                            fill={color}
                            stroke="white"
                            strokeWidth="0.6"
                            vectorEffect="non-scaling-stroke"
                          />
                          <text
                            x="0"
                            y="0"
                            textAnchor="middle"
                            dominantBaseline="central"
                            fontSize="3.2"
                            fontWeight="700"
                            fill="#0B0B0B"
                          >
                            {i + 1}
                          </text>
                        </g>
                      </g>
                    );
                  })}
                  {drawing && drawing.points.length > 1 && (
                    <polyline
                      points={polygonPointsAttr(drawing.points)}
                      fill="rgba(255,255,255,0.08)"
                      stroke="white"
                      strokeWidth="0.6"
                      strokeDasharray="1.2,0.8"
                      vectorEffect="non-scaling-stroke"
                      strokeLinejoin="round"
                    />
                  )}
                </svg>
              </div>

              {/* Hint pill — only show at 1× & before any marking */}
              {zoom === 1 && areas.length === 0 && !drawing && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div className="bg-black/60 backdrop-blur rounded-full px-3 py-1.5 text-[11px] flex items-center gap-1.5 border border-white/10">
                    <MousePointerClick className="w-3 h-3 text-[#D4AF37]" />
                    Click &amp; drag around any region
                  </div>
                </div>
              )}

              {/* Pan-mode badge */}
              {isPanGesture && (
                <div className="absolute top-2 left-2 pointer-events-none">
                  <div className="bg-black/65 backdrop-blur rounded-full px-2.5 py-1 text-[10px] uppercase tracking-[0.18em] text-[#D4AF37] flex items-center gap-1.5 border border-[#D4AF37]/40">
                    <Hand className="w-3 h-3" />
                    {spaceHeld && !panMode ? "Pan · Space" : "Pan mode"} · {zoom.toFixed(1)}×
                  </div>
                </div>
              )}

              {/* Zoom controls — bottom-right of canvas */}
              <div className="absolute bottom-2 right-2 flex items-center gap-1 bg-black/65 backdrop-blur rounded-full p-1 border border-white/10">
                {ZOOM_LEVELS.map((z) => {
                  // Mark closest discrete level when wheel zoom is between them
                  const closest = ZOOM_LEVELS.reduce(
                    (best, lv) => (Math.abs(lv - zoom) < Math.abs(best - zoom) ? lv : best),
                    ZOOM_LEVELS[0]
                  );
                  const active = z === closest;
                  return (
                    <button
                      key={z}
                      type="button"
                      onClick={() => applyZoom(z)}
                      data-testid={`refine-zoom-${z}x`}
                      title={`Zoom to ${z}× · Ctrl/⌘ + scroll for free zoom`}
                      className={`px-2.5 py-1 rounded-full text-[10px] font-medium tracking-wide transition ${
                        active
                          ? "bg-[#D4AF37] text-black"
                          : "text-white/70 hover:text-white hover:bg-white/10"
                      }`}
                    >
                      {z}×
                    </button>
                  );
                })}
                <div className="w-px h-4 bg-white/15 mx-0.5" />
                <button
                  type="button"
                  onClick={() => zoom > 1 && setPanMode((v) => !v)}
                  disabled={zoom === 1}
                  data-testid="refine-pan-toggle"
                  title={
                    zoom === 1
                      ? "Pan available when zoomed in"
                      : panMode
                      ? "Switch back to lasso (or hold Space)"
                      : "Switch to pan mode (or hold Space)"
                  }
                  className={`px-2 py-1 rounded-full text-[10px] flex items-center gap-1 transition disabled:opacity-30 disabled:cursor-not-allowed ${
                    panMode
                      ? "bg-[#D4AF37] text-black"
                      : "text-white/70 hover:text-white hover:bg-white/10"
                  }`}
                >
                  {panMode ? <Hand className="w-3 h-3" /> : <Square className="w-3 h-3" />}
                  {panMode ? "Pan" : "Lasso"}
                </button>
                <button
                  type="button"
                  onClick={() => fitToArea()}
                  disabled={areas.length === 0}
                  data-testid="refine-fit-btn"
                  title={
                    areas.length === 0
                      ? "Lasso an area first, then click Fit (or press F)"
                      : `Fit area ${areas.length} to viewport · F`
                  }
                  className="px-2 py-1 rounded-full text-[10px] flex items-center gap-1 transition text-white/70 hover:text-white hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <Frame className="w-3 h-3" />
                  Fit
                </button>
              </div>
            </div>
            <div className="mt-1.5 flex items-center justify-between text-[10px] text-white/45 lg:hidden">
              <span>{areas.length} / {MAX_AREAS} areas · {quotaRemaining} renders left</span>
              <div className="flex items-center gap-3">
                {drawing && (
                  <button type="button" onClick={undoLastPoint} className="inline-flex items-center gap-1 hover:text-white/80">
                    <Undo2 className="w-3 h-3" /> Undo
                  </button>
                )}
                {areas.length > 0 && (
                  <button onClick={() => setAreas([])} data-testid="refine-clear-areas-mobile" className="hover:text-white/80">
                    Clear
                  </button>
                )}
              </div>
            </div>
            <div className="mt-1.5 hidden lg:flex items-center justify-end gap-3 text-[10px] text-white/45">
              {drawing && (
                <button type="button" onClick={undoLastPoint} className="inline-flex items-center gap-1 hover:text-white/80">
                  <Undo2 className="w-3 h-3" /> Undo point
                </button>
              )}
              {areas.length > 0 && (
                <button onClick={() => setAreas([])} data-testid="refine-clear-areas" className="hover:text-white/80">
                  Clear all
                </button>
              )}
            </div>
          </div>

          {/* Right column: comments + overall + actions — scrolls if too tall */}
          <div className="min-h-0 flex flex-col gap-2.5 lg:overflow-y-auto lg:pr-1">
            <div className="text-[10px] uppercase tracking-[0.22em] text-white/45 flex-shrink-0">Areas to refine</div>

            {/* Recent references library — click a thumb to attach to active area */}
            {refLibrary.length > 0 && (
              <div data-testid="refine-library-row" className="flex-shrink-0 rounded-lg border border-white/8 bg-[#0E0E0E] p-2">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-[9px] uppercase tracking-[0.22em] text-white/40">
                    Recent references
                  </span>
                  <span className="text-[9px] text-white/35">
                    {activeAreaIdx == null
                      ? "Pick an area to attach"
                      : `Tap to attach to area ${activeAreaIdx + 1}`}
                  </span>
                </div>
                <div className="flex gap-1.5 overflow-x-auto pb-1 -mx-0.5 px-0.5">
                  {refLibrary.slice(0, 24).map((r) => (
                    <div
                      key={r.id}
                      className="relative group flex-shrink-0"
                    >
                      <button
                        type="button"
                        onClick={() => {
                          if (activeAreaIdx == null) {
                            toast.error("Click an area on the right first, then a reference.");
                            return;
                          }
                          attachFromLibrary(activeAreaIdx, r);
                        }}
                        data-testid={`refine-library-thumb-${r.id}`}
                        title={`${(r.kind || "reference")} · used ${r.use_count}×`}
                        className={`block w-12 h-12 rounded-md overflow-hidden border ${
                          activeAreaIdx == null
                            ? "border-white/10 opacity-70 hover:opacity-100"
                            : "border-[#D4AF37]/40 hover:border-[#D4AF37] hover:scale-105"
                        } transition`}
                      >
                        <img
                          src={`${BACKEND}${r.image_url}`}
                          alt={r.label || r.kind}
                          className="w-full h-full object-cover"
                        />
                      </button>
                      <button
                        type="button"
                        onClick={() => deleteLibraryRef(r)}
                        data-testid={`refine-library-del-${r.id}`}
                        className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-black/80 border border-white/20 text-white/65 hover:text-rose-300 opacity-0 group-hover:opacity-100 transition flex items-center justify-center"
                        title="Remove from library"
                      >
                        <X className="w-2.5 h-2.5" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {areas.length === 0 ? (
              <div className="rounded-lg border border-dashed border-white/10 bg-[#141414] p-3 text-center flex-shrink-0">
                <p className="text-[11px] text-white/50">No areas yet — drag on the image to start.</p>
              </div>
            ) : (
              <div className="space-y-2 flex-shrink-0">
                {areas.map((a, i) => {
                  const box = areaMeta[i].bbox;
                  const isActive = activeAreaIdx === i;
                  return (
                    <div
                      key={a.id || `area-${i}`}
                      data-testid={`refine-area-${i}`}
                      onClick={() => setActiveAreaIdx(i)}
                      className={`rounded-lg border ${
                        isActive
                          ? "border-[#D4AF37]/60 bg-[#1A1610]"
                          : "border-white/10 bg-[#141414] hover:border-white/20"
                      } p-2.5 cursor-pointer transition`}
                    >
                      <div className="flex items-center justify-between gap-2 mb-1.5">
                        <div className="flex items-center gap-1.5">
                          <span
                            className="w-4 h-4 rounded-full text-black text-[10px] font-bold flex items-center justify-center"
                            style={{ background: BADGE_COLORS[i] }}
                          >
                            {i + 1}
                          </span>
                          <span className="text-[10px] text-white/45">
                            {a.points.length} pts · {Math.round(box.w * 100)}×{Math.round(box.h * 100)}%
                          </span>
                        </div>
                        <button onClick={() => removeArea(i)} className="text-white/40 hover:text-red-400 p-0.5">
                          <X className="w-3 h-3" />
                        </button>
                      </div>

                      {/* Quick-action chips + metal dropdown. Both are
                          optional — they prepend a structured token like
                          `[action=setting metal=yellow_gold]` to the bullet
                          the backend sends to Gemini. The chip is single-
                          select (click again to clear). */}
                      <div className="flex flex-wrap items-center gap-1 mb-1.5">
                        {REFINE_ACTIONS.map((act) => {
                          const active = a.action === act.id;
                          return (
                            <button
                              key={act.id}
                              type="button"
                              onClick={(e) => { e.stopPropagation(); updateAction(i, act.id); }}
                              data-testid={`refine-action-${i}-${act.id}`}
                              className={`px-2 py-0.5 rounded-full text-[10px] tracking-wide transition ${
                                active
                                  ? "bg-[#D4AF37] text-black"
                                  : "bg-white/5 text-white/55 hover:text-white hover:bg-white/10 border border-white/8"
                              }`}
                            >
                              {act.label}
                            </button>
                          );
                        })}
                        <select
                          value={a.metal || ""}
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) => updateMetal(i, e.target.value)}
                          data-testid={`refine-metal-${i}`}
                          className="ml-auto bg-[#0B0B0B] border border-white/10 rounded-full px-2 py-0.5 text-[10px] text-white/75 focus:outline-none focus:border-[#D4AF37] hover:border-white/25 transition"
                          title="Metal colour (prepends a structured token to the prompt)"
                        >
                          <option value="">Metal…</option>
                          {REFINE_METALS.map((m) => (
                            <option key={m.id} value={m.id}>{m.label}</option>
                          ))}
                        </select>
                      </div>

                      <textarea
                        value={a.comment}
                        onChange={(e) => updateComment(i, e.target.value)}
                        placeholder="Describe the change (e.g. 'add a small emerald drop')"
                        rows={2}
                        data-testid={`refine-comment-${i}`}
                        className="w-full bg-[#0B0B0B] border border-white/10 rounded-md px-2.5 py-1.5 text-[12px] text-white placeholder-white/30 focus:outline-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] transition resize-none leading-snug"
                      />

                      {/* Per-area reference image (gemstone / texture / colour) */}
                      <div className="mt-1.5">
                        {a.reference_preview ? (
                          <div
                            data-testid={`refine-ref-preview-${i}`}
                            className="flex items-center gap-2 rounded-md border border-[#D4AF37]/30 bg-[#D4AF37]/5 p-1.5"
                          >
                            <img
                              src={a.reference_preview}
                              alt={`Area ${i + 1} reference`}
                              className="w-9 h-9 rounded-md object-cover border border-white/10 flex-shrink-0"
                            />
                            <div className="flex-1 min-w-0">
                              <select
                                value={a.reference_kind || "gemstone"}
                                onChange={(e) => updateRefKind(i, e.target.value)}
                                data-testid={`refine-ref-kind-${i}`}
                                className="w-full bg-[#0B0B0B] border border-white/10 rounded px-1.5 py-1 text-[10px] text-white focus:outline-none focus:border-[#D4AF37]"
                              >
                                <option value="gemstone">Gemstone reference</option>
                                <option value="texture">Texture / finish</option>
                                <option value="colour">Colour swatch</option>
                              </select>
                              <div className="text-[9px] text-white/45 mt-0.5">
                                AI will apply to this region only
                              </div>
                            </div>
                            <button
                              onClick={() => clearReference(i)}
                              data-testid={`refine-ref-clear-${i}`}
                              className="text-white/45 hover:text-rose-300 p-1 flex-shrink-0"
                              title="Remove reference"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ) : (
                          <label
                            data-testid={`refine-ref-drop-${i}`}
                            onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add("border-[#D4AF37]"); }}
                            onDragLeave={(e) => e.currentTarget.classList.remove("border-[#D4AF37]")}
                            onDrop={(e) => {
                              e.preventDefault();
                              e.currentTarget.classList.remove("border-[#D4AF37]");
                              const f = e.dataTransfer?.files?.[0];
                              if (f) attachReference(i, f);
                            }}
                            className="cursor-pointer flex items-center justify-center gap-1.5 w-full rounded-md border border-dashed border-white/15 bg-[#0B0B0B] px-2 py-1.5 text-[10px] text-white/50 hover:text-white/80 hover:bg-white/5 transition"
                          >
                            <ImagePlus className="w-3 h-3" />
                            <span>Drop gemstone / texture <span className="text-white/30">(optional)</span></span>
                            <input
                              type="file"
                              accept="image/png,image/jpeg,image/webp"
                              data-testid={`refine-ref-input-${i}`}
                              className="hidden"
                              onChange={(e) => attachReference(i, e.target.files?.[0])}
                            />
                          </label>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            <div className="pt-1 flex-shrink-0">
              <div className="text-[10px] uppercase tracking-[0.22em] text-white/45 mb-1.5">
                Overall note (optional)
              </div>
              <textarea
                value={overall}
                onChange={(e) => setOverall(e.target.value)}
                placeholder="Anything else — overall tone, balance, finish?"
                rows={2}
                data-testid="refine-overall-note"
                className="w-full bg-[#141414] border border-white/10 rounded-md px-2.5 py-1.5 text-[12px] text-white placeholder-white/30 focus:outline-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] transition resize-none leading-snug"
              />
            </div>

            {/* Sticky footer inside right column */}
            <div className="mt-auto pt-2 flex-shrink-0 space-y-1.5">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => onOpenChange(false)}
                  className="text-xs px-4 py-2 rounded-full border border-white/15 text-white/75 hover:bg-white/5 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={submit}
                  disabled={submitting || areas.length === 0 || quotaRemaining < 1}
                  data-testid="refine-submit-btn"
                  className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-[#D4AF37] text-black text-xs font-medium py-2 hover:bg-[#F3CE5A] transition disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {submitting ? (
                    <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Refining…</>
                  ) : (
                    <>Refine · 1 render</>
                  )}
                </button>
              </div>
              <p className="text-[10px] text-white/35 text-center lg:hidden">
                {quotaRemaining} renders left this month
              </p>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
